import { useState, useCallback, useEffect, useMemo } from 'react';
import { fetchEntities } from '@/utils/api';
import type { EntitySummary } from '@/types/dashboard';

interface ParsedSourceKey {
  entityType: string;
  bundle: string;
  entityUuid?: string;
}

export interface UseEntityContextReturn {
  contextKey: string;
  selectedEntity: EntitySummary | null;
  availableEntities: EntitySummary[];
  isLoadingEntities: boolean;
  selectEntity: (entity: EntitySummary) => void;
  clearEntity: () => void;
}

export function useEntityContext(sourceKey: string): UseEntityContextReturn {
  const [contextKey, setContextKey] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<EntitySummary | null>(null);
  const [availableEntities, setAvailableEntities] = useState<EntitySummary[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);

  const parsedSourceKey = useMemo((): ParsedSourceKey | null => {
    const parts = sourceKey.split(':');
    if (parts.length < 2) return null;
    const [entityType, bundle, entityUuid] = parts;
    return { entityType, bundle, entityUuid };
  }, [sourceKey]);

  const selectEntity = useCallback((entity: EntitySummary) => {
    if (!parsedSourceKey) return;

    const newContextKey = `${parsedSourceKey.entityType}:${parsedSourceKey.bundle}:${entity.uuid}`;
    setContextKey(newContextKey);
    setSelectedEntity(entity);
  }, [parsedSourceKey]);

  const clearEntity = useCallback(() => {
    setContextKey('');
    setSelectedEntity(null);
  }, []);

  useEffect(() => {
    if (!parsedSourceKey || parsedSourceKey.entityUuid) {
      setAvailableEntities([]);
      setIsLoadingEntities(false);
      return;
    }

    const { entityType, bundle } = parsedSourceKey;
    
    setIsLoadingEntities(true);
    
    fetchEntities(entityType, bundle)
      .then(entities => {
        setAvailableEntities(entities);
      })
      .catch(error => {
        console.error('Failed to fetch entities:', error);
        setAvailableEntities([]);
      })
      .finally(() => {
        setIsLoadingEntities(false);
      });
  }, [parsedSourceKey]);

  useEffect(() => {
    if (parsedSourceKey?.entityUuid) {
      setContextKey(sourceKey);
    } else {
      setContextKey('');
    }
  }, [sourceKey, parsedSourceKey?.entityUuid]);

  return {
    contextKey,
    selectedEntity,
    availableEntities,
    isLoadingEntities,
    selectEntity,
    clearEntity,
  };
}